import { CommonModule, DOCUMENT } from '@angular/common';
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	EventEmitter,
	HostListener,
	Inject,
	Input,
	NgModule,
	Output,
} from '@angular/core';
import { YouTubePlayerModule } from '@angular/youtube-player';

import { ClientScreen, ClientService } from '~app-client/core/services';
import { ReachLogoLoaderModule } from '~app-ui/loader';

import { YoutubePlayerState } from '../types/public_api';

@Component({
	standalone: false,

	selector: 'reach-youtube-player',
	templateUrl: './youtube-player.component.html',
	styleUrls: ['./youtube-player.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: {
		'[class.inline-player]': 'playerVars.playsinline === 1',
	},
})
export class ReachYoutubePlayerComponent {
	@Input()
	public videoId: string;

	@Input()
	public startSeconds?: number;

	@Input()
	public endSeconds?: number;

	@Output()
	public closeVideo = new EventEmitter<void>();

	@Output()
	public videoStateChange = new EventEmitter<YoutubePlayerState>();

	public playerVars: YT.PlayerVars = {
		autoplay: 1,
		playsinline: 0,
		modestbranding: 1,
	};

	@Input()
	public set autoplay(autoplay: boolean) {
		const bool = `${autoplay}` !== `false`;
		this.playerVars.autoplay = bool ? 1 : 0;
	}

	@Input()
	public set inline(inline: boolean) {
		const bool = `${inline}` !== `false`;
		this.playerVars.playsinline = bool ? 1 : 0;
	}

	public loading = true;

	@Input()
	public fullSize = false;

	public readonly baseYoutubePlayerSize = 640;
	public readonly youtubePlayerAspectRatio = 390 / 640;

	private youtubePlayer?: YT.Player;

	constructor(
		@Inject(DOCUMENT) private document: Document,
		private readonly cdRef: ChangeDetectorRef,
		private readonly elementRef: ElementRef<HTMLElement>
	) {}

	public get watchingVideoStartSeconds(): number {
		return 0;
	}

	public get isLandscape(): boolean {
		return ClientService.screen === ClientScreen.DESKTOP;
	}

	public get youtubePlayerWidth(): number {
		if (this.fullSize) {
			return this.elementRef.nativeElement.clientWidth;
		}

		if (this.isLandscape) {
			return Math.min(this.baseYoutubePlayerSize, this.document.body.clientWidth - 2 * 32);
		}

		return Math.min(
			this.document.body.clientWidth - 2 * 32,
			this.youtubePlayerHeight * this.youtubePlayerAspectRatio
		);
	}

	public get youtubePlayerHeight(): number {
		if (this.fullSize) {
			return this.elementRef.nativeElement.clientHeight;
		}

		if (this.isLandscape) {
			return Math.min(
				this.document.body.clientHeight - 2 * 32,
				this.youtubePlayerWidth * this.youtubePlayerAspectRatio
			);
		}

		return Math.min(this.baseYoutubePlayerSize, this.document.body.clientHeight - 2 * 32);
	}

	public onVideoReady(event: YT.PlayerEvent): void {
		this.loading = false;
		this.youtubePlayer = event.target;
		this.emitVideoState();
	}

	public onVideoStateChange(event: YT.OnStateChangeEvent): void {
		switch (event.data) {
			case YT.PlayerState.ENDED: {
				this.onCloseVideo(true);
				break;
			}

			case YT.PlayerState.PLAYING: {
				this.onVideoReady(event);
				break;
			}
		}
	}

	@HostListener('click', ['false'])
	@HostListener('dblclick', ['true'])
	public onCloseVideo(force: boolean | 'true' | 'false'): void {
		if (!this.loading || `${force}` === 'true') {
			this.emitVideoState();
			this.closeVideo.emit();
		}
	}

	private emitVideoState(): void {
		if (this.youtubePlayer) {
			this.videoStateChange.emit({
				id: this.videoId,
				secondsSeen: Math.floor(this.youtubePlayer.getCurrentTime()),
			});
		}
	}

	@HostListener('window:resize')
	public _onResize(): void {
		this.cdRef.detectChanges();
	}
}

@NgModule({
	declarations: [ReachYoutubePlayerComponent],
	imports: [CommonModule, YouTubePlayerModule, ReachLogoLoaderModule],
	exports: [ReachYoutubePlayerComponent],
})
export class ReachYoutubePlayerModule {}
